<template>
  <v-navigation-drawer
    :value="value"
    @input="$emit('input', $event)"
    temporary
    fixed
    right
    width="500"
  >
    <v-card flat>
      <DrawerToolbar title="Create New Report" admin @close="close" />
      <div class="px-4 py-6 d-flex flex-column" style="gap: 24px">
        <v-form ref="reportForm" v-model="report.valid">
          <div class="d-flex flex-column" style="gap: 24px">
            <!-- Shared fields -->
            <v-autocomplete
              outlined
              :items="clients"
              item-text="formattedName"
              item-value="clientId"
              label="Client*"
              v-model="report.clientId"
              :rules="[(v) => !!(v !== undefined && v !== null) || 'Client is required']"
              required
              hide-details="auto"
            ></v-autocomplete>
            <v-text-field
              outlined
              v-model="report.name"
              :rules="[(v) => !!v || 'Report name is required']"
              label="Name*"
              required
              hide-details="auto"
            ></v-text-field>
            <v-text-field
              outlined
              v-model="report.displayName"
              :rules="[(v) => !!v || 'Display name is required']"
              label="Display Name (Visible to Client)*"
              required
              hide-details="auto"
            ></v-text-field>
            <v-text-field
              outlined
              v-model="report.description"
              label="Description"
              hide-details="auto"
            ></v-text-field>
            <!-- <v-checkbox
              label="Launch on page load"
              v-model="report.openOnLoad"
              class="mt-0"
              hide-details
            /> -->
            <v-select
              v-if="isPowerBiReportsEnabled"
              outlined
              v-model="report.provider"
              :items="providerOptions"
              :rules="[(v) => !!v || 'Provider is required']"
              label="Provider"
              required
              hide-details="auto"
            ></v-select>

            <!-- Looker Studio fields -->
            <template v-if="report.provider === 'LOOKER'">
              <v-select
                outlined
                v-model="report.type"
                :items="typeOptions"
                :rules="[(v) => !!v || 'Type is required']"
                label="Type"
                required
                hide-details="auto"
              ></v-select>
              <v-text-field
                outlined
                v-model="report.reportUrl"
                :rules="[(v) => !!v || 'Report URL is required']"
                label="Report URL*"
                required
                hide-details="auto"
              ></v-text-field>
              <v-text-field
                outlined
                v-model="report.tokenTTL"
                label="Token Time to Live"
                suffix="seconds"
                :rules="[
                  (v) => !!v || 'Token TTL is required',
                  (v) =>
                    (!!v && !isNaN(v) && parseInt(v) > 0 && !v.includes('.')) ||
                    'Token TTL must be a positive, whole number',
                ]"
                hide-details="auto"
              ></v-text-field>
              <v-checkbox
                label="Open in new tab"
                v-model="report.openInNewTab"
                class="mt-0"
                hide-details
              />
            </template>

            <!-- Power BI fields -->
            <template v-if="report.provider === 'POWERBI'">
              <v-text-field
                outlined
                v-model="report.powerbiWorkspaceId"
                :rules="formRules.guidRequired('Workspace ID')"
                label="Workspace ID*"
                placeholder="XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX"
                hint="The GUID after /groups/ in the report URL"
                persistent-hint
                required
                hide-details="auto"
              ></v-text-field>
              <v-text-field
                outlined
                v-model="report.powerbiReportId"
                :rules="formRules.guidRequired('Report ID')"
                label="Report ID*"
                placeholder="XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX"
                required
                hide-details="auto"
              ></v-text-field>
              <v-textarea
                outlined
                v-model="report.powerbiParams"
                label="Parameters (JSON)"
                :rules="[paramsRule]"
                rows="6"
                hide-details="auto"
                hint='Optional JSON array. Each item maps a JWT value (or static default_value) to a Power BI filter. Add an "operator" (e.g. LessThanOrEqual) for range/comparison filters; omit it for an exact match.'
                persistent-hint
                placeholder='[{ "parameter_name": "Daily Users/DaysFromToday", "operator": "LessThanOrEqual", "source_type": "jwt_metadata", "source_key": "static", "required": false, "default_value": 30 }]'
              ></v-textarea>
            </template>
          </div>
        </v-form>
        <div class="d-flex" style="gap: 16px">
          <Button text @click="close">Cancel</Button>
          <Button
            color="brandPrimary"
            dark
            depressed
            rounded
            @click="createReport"
            :disabled="!report.valid"
            :loading="submitting"
          >
            Create Report
          </Button>
        </div>
      </div>
    </v-card>
  </v-navigation-drawer>
</template>

<script>
import ReportService from "@/services/ReportService.js";
import PowerBIReportService from "@/services/PowerBIReportService.js";
import Button from "@/components/generic/Button.vue";
import DrawerToolbar from "@/components/generic/DrawerToolbar.vue";
import { formRules } from "@/shared_data/data.js";
import { mapState } from "vuex";

export default {
  name: "CreateReportForm",
  components: {
    Button,
    DrawerToolbar,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    clients: {
      type: Array,
      default: () => [],
    },
    userId: {
      type: String,
      default: null,
    },
    defaultClientId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      formRules,
      submitting: false,
      report: this.getEmptyReport(),
      providerOptions: [
        { value: "LOOKER", text: "Looker Studio" },
        { value: "POWERBI", text: "Power BI" },
      ],
      typeOptions: [
        { value: "GENERAL", text: "General" },
        { value: "PROCORE", text: "Procore" },
      ],
    };
  },
  computed: {
    ...mapState(["featureFlags"]),
    isPowerBiReportsEnabled() {
      return this.featureFlags["feature-power-bi-reports"];
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.resetForm();
      }
    },
  },
  methods: {
    getEmptyReport() {
      return {
        valid: false,
        provider: null,
        clientId: null,
        name: null,
        displayName: null,
        description: null,
        // Looker-only
        reportUrl: null,
        tokenTTL: null,
        type: "GENERAL",
        openInNewTab: false,
        openOnLoad: false,
        // Power BI-only
        powerbiWorkspaceId: null,
        powerbiReportId: null,
        powerbiParams: "", // raw JSON text for the parameters array
      };
    },
    resetForm() {
      this.report = this.getEmptyReport();
      this.report.clientId = this.defaultClientId;
      // When Power BI reports are disabled, the provider select is hidden and
      // we default to Looker Studio so only the Looker/generic fields show.
      if (!this.isPowerBiReportsEnabled) {
        this.report.provider = "LOOKER";
      }
      if (this.$refs.reportForm) {
        this.$refs.reportForm.resetValidation();
      }
    },
    close() {
      this.$emit("input", false);
    },
    // Validates the optional Parameters textarea: empty, or a JSON array.
    paramsRule(v) {
      if (!v || !v.trim()) return true;
      try {
        const parsed = JSON.parse(v);
        return Array.isArray(parsed) || "Parameters must be a JSON array";
      } catch (e) {
        return "Parameters must be valid JSON";
      }
    },
    async createReport() {
      this.submitting = true;
      try {
        if (this.report.provider === "LOOKER") {
          await ReportService.createReport({
            clientId: this.report.clientId,
            name: this.report.name,
            displayName: this.report.displayName,
            description: this.report.description,
            reportUrl: this.report.reportUrl,
            openOnLoad: this.report.openOnLoad,
            openInNewTab: this.report.openInNewTab,
            type: this.report.type,
            status: "Active",
            createdBy: this.userId,
            updatedBy: this.userId,
            tokenTTL: this.report.tokenTTL,
          });
        } else {
          // Parameters are optional; the form rule guarantees valid JSON, but
          // guard anyway so a bad value never silently sends a broken config.
          const rawParams = (this.report.powerbiParams || "").trim();
          const parameters = rawParams ? JSON.parse(rawParams) : undefined;

          await PowerBIReportService.createConfig({
            clientId: this.report.clientId,
            name: this.report.name,
            displayName: this.report.displayName,
            description: this.report.description,
            workspaceId: this.report.powerbiWorkspaceId,
            reportId: this.report.powerbiReportId,
            enabled: true,
            createdBy: this.userId,
            ...(parameters ? { parameters } : {}),
          });
        }
        this.$emit("created");
        this.close();
      } catch (error) {
        console.log("Error creating report", error);
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
